import * as React from 'react'
import moment from 'moment'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { FaIcon, faPlus, faTrash, faArrowsRotate } from '@/lib/fa'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import SearchColor from '@/assets/icons_new/SearchColor.svg'
import { useQueryCurrencies } from '@/modules/shared/query-client/useQueryCurrencies'
import { PaymentMethod } from '@/app/constants/delegation/paymentMetod'
import InvoiceRowItem from './InvoiceRowItem'

interface BusinessTripEditInvoicesProps {
  tripData: any
  onUpdate: (field: string, value: any) => void
  onUpdateArrayItem?: (arrayName: string, index: number, updates: any) => void
  onAddArrayItem?: (arrayName: string, newItem: any) => void
  onRemoveArrayItems?: (arrayName: string, indices: number[]) => void
  companyId: string
  validationErrors?: Record<string, any>
  onGenerateInvoices?: () => void
}

const invoicesColumnKeys = [
  { titleKey: 'businessTrip.col-cost-date', key: 'costDate', type: 'string', sort: false },
  { titleKey: 'businessTrip.col-invoice-number', key: 'invoiceNumber', type: 'string', sort: false },
  { titleKey: 'businessTrip.col-name', key: 'name', type: 'string', sort: false },
  { titleKey: 'businessTrip.col-amount', key: 'amount', type: 'number', sort: false },
  { titleKey: 'businessTrip.col-currency', key: 'currency', type: 'string', sort: false },
  { titleKey: 'businessTrip.col-exchange-rate', key: 'exchangeRate', type: 'number', sort: false },
  { titleKey: 'businessTrip.col-exchange-rate-date', key: 'exchangeRateDate', type: 'string', sort: false },
  { titleKey: 'businessTrip.col-table', key: 'table', type: 'string', sort: false },
  { titleKey: 'businessTrip.col-payment-method', key: 'paymentMethod', type: 'string', sort: false },
  { titleKey: 'businessTrip.col-amount-pln', key: 'plnAmount', type: 'number', sort: false },
] as const

const BusinessTripEditInvoices: React.FC<BusinessTripEditInvoicesProps> = ({
  tripData,
  onUpdate,
  onUpdateArrayItem,
  onAddArrayItem,
  onRemoveArrayItems,
  companyId,
  onGenerateInvoices,
}) => {
  const { t } = useLocalize()
  const InvoicesColumns: TableHeaderColumn[] = React.useMemo(() =>
    invoicesColumnKeys.map(({ titleKey, ...rest }) => ({ ...rest, title: t(titleKey) })),
    [t],
  )
  const [searchValue, setSearchValue] = React.useState('')
  const [selectedIds, setSelectedIds] = React.useState<number[]>([])
  const [sortConfig, setSortConfig] = React.useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)
  const [showSearch, setShowSearch] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  const { data: currencies = [] } = useQueryCurrencies({ companyId })

  // Click outside to close search
  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setSearchValue('')
        setShowSearch(false)
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  const invoices = tripData?.invoices || []

  const filteredInvoices = React.useMemo(() => {
    if (!searchValue) return invoices
    const search = searchValue.toLowerCase()
    return invoices.filter((invoice: any) => {
      return (
        invoice.name?.toLowerCase().includes(search) ||
        invoice.invoiceNumber?.toLowerCase().includes(search) ||
        invoice.table?.toLowerCase().includes(search)
      )
    })
  }, [invoices, searchValue])

  const getOriginalIndex = (invoice: any): number => {
    const idx = invoices.indexOf(invoice)
    if (idx !== -1) return idx
    return invoices.findIndex((inv: any) => {
      if (inv.id != null && invoice.id != null && String(inv.id) === String(invoice.id)) return true
      if (inv.lp != null && invoice.lp != null && inv.lp !== -1 && invoice.lp !== -1 && inv.lp === invoice.lp) return true
      return false
    })
  }

  const toggleAll = (checked: boolean) => {
    if (checked) {
      const indices = filteredInvoices
        .map((i: any) => getOriginalIndex(i))
        .filter((i) => i !== -1)
      setSelectedIds(indices)
    } else {
      setSelectedIds([])
    }
  }

  const toggleId = (id: number) => {
    if (id === -1) return
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const handleHeaderClick = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }

  const handleAdd = () => {
    if (onAddArrayItem) {
      onAddArrayItem('invoices', {
        lp: -1,
        costDate: moment().format('YYYY-MM-DD'),
        invoiceNumber: '',
        name: '',
        amount: 0,
        currency: currencies.find((c: any) => c.code === 'PLN') || { code: 'PLN', id: null },
        exchangeRate: 1,
        exchangeRateDate: moment().format('YYYY-MM-DD'),
        table: '',
        paymentMethod: PaymentMethod.Cash,
        plnAmount: 0,
        enteredManually: false,
      })
    }
  }

  const handleDelete = () => {
    if (selectedIds.length > 0) {
      const indicesToRemove = new Set(selectedIds)
      const updated = invoices.filter((_: any, i: number) => !indicesToRemove.has(i))
      onUpdate('invoices', updated)
      setSelectedIds([])
    }
  }

  const isSelectedAny = selectedIds.length > 0

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-rounded-lg tw-bg-white">
      {/* Action Bar */}
      <div className="tw-flex tw-w-full tw-shrink-0 tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-bg-[#fafbff] tw-px-4 tw-py-3">
          {/* Left side - Actions */}
          <div className="tw-flex tw-items-center tw-gap-4">
            {/* Search button with dropdown */}
            <div className="tw-relative" ref={containerRef}>
              <div
                className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-2 tw-px-1 ${
                  showSearch ? 'tw-rounded-lg tw-border-primary' : 'tw-border-transparent'
                }`}
                onClick={() => setShowSearch((prev) => !prev)}
              >
                <img src={SearchColor} alt="Search Icon" />
                <p className="tw-text-sm tw-font-medium tw-text-primary">
                  {t('operation.find') || 'Wyszukaj'}
                </p>
              </div>
              {showSearch && (
                <div className="tw-absolute tw-z-50 tw-mt-2 tw-w-[320px] tw-rounded-lg tw-bg-white tw-p-2 tw-shadow-md">
                  <div className="tw-relative tw-w-full">
                    <input
                      autoFocus
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      placeholder={t('operation.find') || 'Szukaj...'}
                      className="tw-w-full tw-rounded tw-border tw-border-neutral-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="tw-absolute tw-right-2 tw-top-1/2 tw-flex tw--translate-y-1/2 tw-items-center tw-gap-2">
                      {searchValue && (
                        <>
                          <span className="tw-text-sm tw-text-gray-500">
                            {filteredInvoices.length}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              setSearchValue('')
                            }}
                            className="tw-text-gray-400 hover:tw-text-gray-700"
                          >
                            ✕
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Add button */}
            <div
              className="tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6] tw-cursor-pointer"
              onClick={handleAdd}
              title={t('answer.add') || 'Dodaj'}
            >
              <FaIcon icon={faPlus} className="tw-h-4 tw-w-4 tw-text-[#2448bb]" />
              <span className="tw-text-sm tw-font-medium tw-text-[#2448bb]">
                {t('answer.add') || 'Dodaj'}
              </span>
            </div>

            {/* Generate invoices button */}
            <div
              className="tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6] tw-cursor-pointer"
              onClick={onGenerateInvoices}
              title={'Generuj Koszty'}
            >
              <FaIcon icon={faArrowsRotate} className="tw-h-4 tw-w-4 tw-text-[#2448bb]" />
              <span className="tw-text-sm tw-font-medium tw-text-[#2448bb]">
                Generuj Koszty
              </span>
            </div>

            {/* Delete button */}
            {isSelectedAny && (
              <div
                className="tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6] tw-cursor-pointer"
                onClick={handleDelete}
                title={t('operation.delete')}
              >
                <FaIcon icon={faTrash} className="tw-h-4 tw-w-4 tw-text-error" />
                <span className="tw-text-sm tw-font-medium tw-text-error">
                  {t('operation.delete')} ({selectedIds.length})
                </span>
              </div>
            )}
          </div>
        </div>

      {/* Invoices table — fills remaining vertical space, scrolls inside */}
      <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto tw-border-t tw-border-base-300">
        <table className="tw-w-full tw-table-auto tw-border-collapse">
          <TableHeader
            columns={InvoicesColumns}
            fontSize="md"
            isSelectedAny={isSelectedAny}
            rounded
            sticky
            sortConfig={sortConfig}
            bolder
            disabledToggle={false}
            toggleAll={toggleAll}
            onHeaderClick={handleHeaderClick}
          />
          <tbody>
            {filteredInvoices.length > 0 ? (
              filteredInvoices.map((invoice: any, index: number) => {
                const originalIndex = getOriginalIndex(invoice)
                const isChecked = selectedIds.includes(originalIndex)

                return (
                  <InvoiceRowItem
                    key={
                      originalIndex !== -1 ? originalIndex : `temp-${index}`
                    }
                    companyId={companyId}
                    onUpdateArrayItem={onUpdateArrayItem}
                    isChecked={isChecked}
                    toggleId={toggleId}
                    originalIndex={originalIndex}
                    invoice={invoice}
                    currencies={currencies}
                  />
                )
              })
            ) : (
              <tr>
                <td colSpan={InvoicesColumns.length + 1} className="tw-p-8 tw-text-center">
                  <p className="tw-text-content-base tw-opacity-70">
                    {t('businessTrip.no-data')}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default BusinessTripEditInvoices
